import {
  convertBuilderJson,
  withRootNode,
  createDefaultTextStyle,
} from '@kizen/mjml/convert';
import { reassignIds } from '@kizen/page-builder/utils/id';
import { camelToSnakeCaseKeys } from 'services/helpers';
import { needsConversion } from 'ts-filters/utils';
import { SENDER_TYPE } from './components';
import { convertToTeamOption } from 'utility/TransformToSelectOptions';

const {
  LAST_ROLE,
  TEAM_MEMBER,
  BUSINESS,
  TEAM_MEMBER_FIELD,
  SPECIFIC_INTEGRATED_INBOX,
} = SENDER_TYPE;

export const cleanCraftJsContent = (craftJsContent) => {
  const { kzn_filters, ...rest } = craftJsContent ?? {};
  return rest;
};

const createFilterPayload = (filter) => {
  if (needsConversion(filter.filter)) {
    return {
      ...filter.filter,
      query: filter.filter.query.map((q) => {
        return {
          ...q,
          filters: q.filters.map((filter) => {
            return camelToSnakeCaseKeys(filter.values);
          }),
        };
      }),
    };
  }
  return filter.filter;
};

/**
 * We're reassigning all image ids here for the dynamic images feature
 * The class names added to the generated email HTML contain the craft
 * node ids. CSS classes cannot have underscores and the default nanoid
 * id generator was including underscores.
 */
export const getCraftJsonWithSafeDynamicImageIds = (craftjson) => {
  return reassignIds(craftjson, 'Image');
};

const getFiltersPayloadFromJson = (craftjson) => {
  const keys = {
    in_group: 'in_group_ids',
    not_in_group: 'not_in_group_ids',
    custom_filter: 'custom_filters',
  };
  return Object.values(craftjson).reduce((acc, { type, custom }) => {
    if (type.resolvedName === 'Image' && Array.isArray(custom.dynamicImages)) {
      for (const node of custom.dynamicImages) {
        const key = keys[node.custom.filter.type];
        acc[node.props.filterId] = {
          [key]:
            node.custom.filter.type === 'custom_filter'
              ? createFilterPayload(node.custom.filter)
              : node.custom.filter.groups.map((maybeGroup) => {
                  return typeof maybeGroup === 'string'
                    ? maybeGroup
                    : maybeGroup?.id;
                }),
        };
      }
    }
    if (type.resolvedName === 'Text' && Array.isArray(custom.dynamicText)) {
      for (const node of custom.dynamicText) {
        const key = keys[node.custom.filter.type];
        acc[node.props.filterId] = {
          [key]:
            node.custom.filter.type === 'custom_filter'
              ? node.custom.filter.filter
              : node.custom.filter.groups,
        };
      }
    }
    return acc;
  }, {});
};

export const getMjmlJson = (
  editor,
  { processDynamicImages, processDynamicText } = {}
) => {
  const craftJsContent = getCraftJsonWithSafeDynamicImageIds(
    editor?.query?.getSerializedNodes()
  );
  const [body, { headAttributes, headChildren }] = convertBuilderJson(
    craftJsContent,
    {
      processDynamicImages,
      processDynamicText,
    }
  );
  const [textStyle, textAttr] = createDefaultTextStyle();
  return withRootNode(body, {
    headAttributes: headAttributes.concat(textAttr),
    headChildren: headChildren.concat(textStyle),
  });
};

// translate message response
export const translateMessageResponse = (
  craftJsContent,
  kznFilters,
  htmlContent,
  {
    senderType,
    sendFrom,
    customName,
    subjectLine,
    internalName,
    selectedSummary,
    integratedInbox,
  }
) => {
  return {
    craftJsContent,
    kznFilters,
    htmlContent,
    ...selectedSummary,
    sender: {
      type: senderType,
      id:
        senderType === SENDER_TYPE.SPECIFIC_INTEGRATED_INBOX
          ? integratedInbox?.value || integratedInbox?.id
          : sendFrom,
      customName: customName.length ? customName.trim() : null,
    },
    subject: subjectLine.trim(),
    name: internalName.trim() || subjectLine.trim(),
  };
};

export const buildResponse = async (
  editor,
  mjml2html,
  {
    senderType,
    sendFrom,
    customName,
    subjectLine,
    internalName,
    selectedSummary,
    integratedInbox,
  }
) => {
  const craftJsContent = getCraftJsonWithSafeDynamicImageIds(
    editor?.query?.getSerializedNodes()
  );
  const [body, { headAttributes, headChildren }] =
    convertBuilderJson(craftJsContent);
  const [textStyle, textAttr] = createDefaultTextStyle();
  const { html } = await mjml2html(
    withRootNode(body, {
      headAttributes: headAttributes.concat(textAttr),
      headChildren: headChildren.concat(textStyle),
    })
  );

  return translateMessageResponse(
    craftJsContent,
    getFiltersPayloadFromJson(craftJsContent),
    html,
    {
      senderType,
      sendFrom,
      customName,
      subjectLine,
      internalName,
      selectedSummary,
      integratedInbox,
    }
  );
};

export const getExternalSendFrom = (sender, t) =>
  sender?.label || sender?.email || t('[Deleted]');

export const getSelectedSummary = (
  senderType,
  teamMembers,
  rolesOptions,
  businessName,
  sendFrom,
  sendFromTeamMemberField,
  integratedInbox
) => {
  if (senderType === TEAM_MEMBER) {
    const teamMember = teamMembers.find(({ value }) => value === sendFrom);
    return {
      senderTeamMember: teamMember?.data,
    };
  } else if (senderType === LAST_ROLE) {
    return { senderRole: rolesOptions.find(({ value }) => value === sendFrom) };
  } else if (senderType === BUSINESS) {
    return { businessName };
  } else if (senderType === TEAM_MEMBER_FIELD) {
    return { senderField: sendFromTeamMemberField };
  } else if (senderType === SPECIFIC_INTEGRATED_INBOX) {
    return { externalAccount: integratedInbox };
  }
  return null;
};

export const getTeamMemberValue = (
  sendFrom,
  senderTeamMember,
  defaultMember,
  sendFromOption
) => {
  if (sendFromOption) {
    return sendFromOption;
  }
  if (sendFrom === senderTeamMember?.id) {
    return convertToTeamOption(senderTeamMember);
  } else if (sendFrom === defaultMember.id) {
    return convertToTeamOption(defaultMember);
  }

  return sendFrom;
};

export const getCategorizedFieldOptions = (categorizedFields = []) =>
  categorizedFields.reduce((acc, category) => {
    if (category.fields.length > 0) {
      acc.push({
        label: category.name,
        options: category.fields.map((item) => {
          return {
            label: item.displayName,
            value: item.id,
            item,
          };
        }),
      });
    }
    return acc;
  }, []);
